import api from '@/libs/axiosInstance';
import type { ApiResponse, BrandSelfSignupStatus } from '@fluenceur/types';
import { useQuery } from '@tanstack/react-query';

/** Retries of a failed read, before the query settles in error. */
export const SELF_SIGNUP_STATUS_RETRIES = 3;

/**
 * Whether a brand can sign up on its own (`GET /auth/brands/self-signup`, public).
 *
 * A failed read (a 429, a dropped connection) says nothing about the switch, so
 * it is never turned into "closed": the error surfaces and is retried, and the
 * pages reading the switch wait while it is unknown rather than send the brand
 * away on a guess.
 */
export const useBrandSelfSignupStatusQuery = () =>
  useQuery<BrandSelfSignupStatus>({
    queryKey: ['brandSelfSignupStatus'],
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<unknown>>('/auth/brands/self-signup');
      const status = data.data as Partial<BrandSelfSignupStatus> | undefined;
      if (
        data.success !== true ||
        !status ||
        typeof status.enabled !== 'boolean' ||
        typeof status.cardRequiredOutsideShopify !== 'boolean'
      ) {
        throw new Error('Invalid brand signup configuration');
      }
      return {
        enabled: status.enabled,
        cardRequiredOutsideShopify: status.cardRequiredOutsideShopify,
      };
    },
    staleTime: 5 * 60 * 1000,
    retry: SELF_SIGNUP_STATUS_RETRIES,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
  });
